import { type CallsClient, CallsError } from '@wtfalch/calls';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { guestDisplayName } from './guestName.ts';
import { describeCallError } from './useCallEndpoint.ts';

/** How often the people at the door are asked for while a member is in the call. */
export const LOBBY_POLL_MS = 4000;

export type WaitingGuest = { admissionId: string; name: string; waitingSince: number };

export type LobbyState = {
  waiting: WaitingGuest[];
  /** Why the list could not be read, or null. */
  error: string | null;
  /** Per guest: what the last Admit or Refuse on them said. */
  notes: Readonly<Record<string, string>>;
  /** Guests with an answer in flight. */
  busy: ReadonlySet<string>;
  /** "Ingrid is waiting to join", until dismissed or the lobby is looked at. */
  notice: string | null;
  dismissNotice: () => void;
  admit: (admissionId: string) => void;
  refuse: (admissionId: string) => void;
};

/** The words for an answer the service refused. `handled` means another
 *  member got there first, which is not an error. */
export function describeLobbyError(error: unknown): { text: string; handled: boolean } {
  if (error instanceof CallsError) {
    if (error.reason === 'admission_expired' || error.code === 'not_found') {
      return { text: 'Already handled.', handled: true };
    }
    if (error.code === 'forbidden') {
      return { text: 'You cannot let people into this call.', handled: false };
    }
    if (error.reason === 'capacity_reached') {
      return { text: 'This call is full (25 people). They are still waiting.', handled: false };
    }
  }
  return { text: describeCallError(error, 'Could not answer. Try again.'), handled: false };
}

/**
 * The guests waiting to be let in, for a member who is in the call: asked for
 * every few seconds while `active`, answered with Admit or Refuse. A guest's
 * name is untrusted text and leaves here already cleaned.
 */
export function useLobby(
  client: CallsClient,
  sessionId: string,
  active: boolean,
  intervalMs = LOBBY_POLL_MS,
): LobbyState {
  const [waiting, setWaiting] = useState<WaitingGuest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const seen = useRef<Set<string> | null>(null);
  const reading = useRef(false);

  const read = useCallback(async () => {
    if (reading.current) return;
    reading.current = true;
    try {
      const { waiting: rows } = await client.waiting(sessionId);
      const next = rows.map((row) => ({
        admissionId: row.admissionId,
        name: guestDisplayName(row.displayName),
        waitingSince: row.waitingSince,
      }));
      setWaiting(next);
      setError(null);
      // The first read only learns who is already there; after it, anyone new
      // is news.
      if (seen.current) {
        const fresh = next.filter((guest) => !seen.current?.has(guest.admissionId));
        const first = fresh[0];
        if (first) {
          setNotice(
            fresh.length === 1
              ? `${first.name} is waiting to join.`
              : `${fresh.length} people are waiting to join.`,
          );
        }
      }
      seen.current = new Set([...(seen.current ?? []), ...next.map((guest) => guest.admissionId)]);
    } catch (failure) {
      setError(
        failure instanceof CallsError && failure.code === 'forbidden'
          ? 'You cannot see who is waiting.'
          : 'Could not check who is waiting.',
      );
    } finally {
      reading.current = false;
    }
  }, [client, sessionId]);

  useEffect(() => {
    if (!active) return;
    seen.current = null;
    void read();
    const timer = setInterval(() => void read(), intervalMs);
    return () => {
      clearInterval(timer);
      setWaiting([]);
      setNotice(null);
    };
  }, [active, read, intervalMs]);

  const answer = useCallback(
    (admissionId: string, act: () => Promise<unknown>) => {
      setBusy((current) => new Set(current).add(admissionId));
      setNotes((current) => {
        const { [admissionId]: _gone, ...rest } = current;
        return rest;
      });
      act()
        .then(
          () => {
            setWaiting((current) => current.filter((guest) => guest.admissionId !== admissionId));
          },
          (failure: unknown) => {
            const { text, handled } = describeLobbyError(failure);
            if (handled) {
              setWaiting((current) => current.filter((guest) => guest.admissionId !== admissionId));
            } else {
              setNotes((current) => ({ ...current, [admissionId]: text }));
            }
          },
        )
        .finally(() => {
          setBusy((current) => {
            const next = new Set(current);
            next.delete(admissionId);
            return next;
          });
          void read();
        });
    },
    [read],
  );

  const admit = useCallback(
    (admissionId: string) =>
      answer(admissionId, () =>
        client.admitGuest(sessionId, { admissionId, video: true, requestId: crypto.randomUUID() }),
      ),
    [answer, client, sessionId],
  );
  const refuse = useCallback(
    (admissionId: string) =>
      answer(admissionId, () =>
        client.removeParticipant(sessionId, { admissionId, requestId: crypto.randomUUID() }),
      ),
    [answer, client, sessionId],
  );

  const dismissNotice = useCallback(() => setNotice(null), []);

  return useMemo(
    () => ({ waiting, error, notes, busy, notice, dismissNotice, admit, refuse }),
    [waiting, error, notes, busy, notice, dismissNotice, admit, refuse],
  );
}
